import React, { useMemo } from 'react';
import { CloseIcon } from '@chakra-ui/icons';
import { Center, Flex, IconButton, Text, useColorModeValue, useDisclosure, useToast } from '@chakra-ui/react';
import { addSeconds } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { AlertDialogModal } from 'clipsal-cortex-ui/src/components/AlertDialogModal';
import { formatDate } from 'clipsal-cortex-utils/src/formatting/formatting';

import { SEHomeCard } from '../../../../common/components/SEHomeCard';
import { selectSite } from '../../../site/siteSlice';
import { getFormattedTime } from '../../settings/settings-helpers';
import { useUpdateVPPEventMutation } from '../vppApi';

type Props = {
  inverterId: number;
  programName: string;
  vppEventId: string;
  vppEventStart: string;
  vppEventDuration: number; // in seconds
};

export function VPPEventListItem({
  inverterId,
  programName,
  vppEventStart,
  vppEventId,
  vppEventDuration,
  ...props
}: Props) {
  const textColor = useColorModeValue('customGrey.800', 'customGrey.300');
  const { t } = useTranslation();
  const { timezone } = useSelector(selectSite);

  const { isOpen: isOptOutDialogOpen, onOpen: onOpenOptOutDialog, onClose: onCloseOptoutDialog } = useDisclosure();
  const [updateVPPEvent] = useUpdateVPPEventMutation();
  const toast = useToast({ duration: 3000, isClosable: true });

  const vppEventStartDate = useMemo(() => toZonedTime(new Date(vppEventStart), timezone), [timezone, vppEventStart]);
  const vppEventStartAndEndString = useMemo(() => {
    const vppEventEndDate = addSeconds(vppEventStartDate, vppEventDuration);
    return `${formatDate(vppEventStartDate)} ${getFormattedTime(vppEventStartDate)}-${getFormattedTime(
      vppEventEndDate
    )}`;
  }, [vppEventDuration, vppEventStartDate]);

  const handleOptOutOfVPPEvent = async () => {
    try {
      await updateVPPEvent({ inverterId, vppEventId, body: { event_status: 'OptOut' } }).unwrap();
    } catch (error) {
      console.error(error);
      toast({
        status: 'error',
        title: t('Common.something went wrong'),
        description: `${t('Common.please try again')} ${t('Common.if this persists contact support')}`,
      });
    }
    onCloseOptoutDialog();
  };

  return (
    <>
      <SEHomeCard
        cursor="pointer"
        p={4}
        borderTopLeftRadius="5px"
        mb={2}
        data-testid={`event-card-${vppEventId}`}
        {...props}
      >
        <Center justifyContent="space-between">
          <Flex direction="column">
            <Text fontSize={17} fontWeight={400} lineHeight={1.2} overflowWrap="anywhere">
              {programName}
            </Text>
            <Text fontSize={15} color={textColor} fontWeight={400} lineHeight={1.1}>
              {vppEventStartAndEndString}
            </Text>
          </Flex>
          <IconButton
            onClick={onOpenOptOutDialog}
            bg="none"
            justifyContent="center"
            aria-label="Cancel"
            icon={<CloseIcon boxSize={3} />}
            data-testid={`opt-out-vpp-btn-${vppEventId}`}
            height="100%"
            p={2}
          />
        </Center>
      </SEHomeCard>
      <AlertDialogModal
        isOpen={isOptOutDialogOpen}
        onClose={onCloseOptoutDialog}
        header={t('VPP.vpp event opt out title')}
        subHeader={t('VPP.vpp event opt out description')}
        confirmButtonName={t('VPP.opt out')}
        cancelButtonName={t('Common.cancel')}
        onConfirm={handleOptOutOfVPPEvent}
      />
    </>
  );
}
